import React, { useState, useEffect, useRef, useMemo } from "react";
import { Popover, Select, MenuItem, FormControl } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import "../analytics/analytics.scss";
import "./BasicAnalytics.scss";
import PatientStats from "../../components/analytics_new/PatientStats";
import CtgSessions from "../../components/analytics_new/CtgSessions";
import MonitoringStats from "../../components/analytics_new/MonitoringStats";
import Cookie from "js-cookie";
import urls from "../../utils/url";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import Loading from "../../components/common/loading";
import { useResetOnBack } from "../../hooks/useResetOnBack";
import AnalyticsStatCards from "../../components/analytics_new/AnalyticsStatCards";
import StatusDonutCard from "../../components/analytics_new/StatusDonutCard";
import AlertOverview from "../../components/analytics_new/AlertOverview";
import UniquePatientMonitored from "../../components/analytics_new/UniquePatientMonitored";

const CustomDay = ({ startDate, endDate, day, ...props }) => {
  const isStart = startDate && day.isSame(startDate, "day");
  const isEnd = endDate && day.isSame(endDate, "day");
  const isInRange =
    startDate &&
    endDate &&
    day.isAfter(startDate, "day") &&
    day.isBefore(endDate, "day");

  return (
    <PickersDay
      {...props}
      day={day}
      sx={{
        ...(isInRange && {
          backgroundColor: "#F6F2FF !important",
          borderRadius: "0 !important",
          color: "rgba(108, 23, 217, 1) !important",
        }),
        ...((isStart || isEnd) && {
          backgroundColor: "rgba(232, 212, 255, 1) !important",
          color: "#1a1a1a  !important",
          borderRadius: "50% !important",
        }),
      }}
    />
  );
};

const RECORDING_BUCKETS = [
  { key: "recording_1", label: "1 recording" },
  { key: "recording_2", label: "2 recordings" },
  { key: "recording_3_4", label: "3-4 recordings" },
  { key: "recording_5_plus", label: "5+ recordings" },
];

const DATE_FILTER_LABELS = {
  all: "Till Date",
  month: "Current Month",
  day: "Today",
  yesterday: "Yesterday",
  week: "Last 7 days",
  last15days: "Last 15 Days",
  last30days: "Last 30 Days",
};

const aggregateDeviceStatus = (facilities) =>
  facilities.reduce(
    (acc, f) => ({
      active: acc.active + (f.device_status?.active || 0),
      disconnected: acc.disconnected + (f.device_status?.disconnected || 0),
    }),
    { active: 0, disconnected: 0 },
  );

const aggregateTraceCategory = (facilities) =>
  facilities.reduce(
    (acc, f) => ({
      normal: acc.normal + (f.trace_category?.normal || 0),
      suspicious: acc.suspicious + (f.trace_category?.suspicious || 0),
      pathological: acc.pathological + (f.trace_category?.pathological || 0),
    }),
    { normal: 0, suspicious: 0, pathological: 0 },
  );

// No per-district/per-scope API aggregate for this, so sum counts +
// total_unique_patients across the given facilities and recompute
// percentages from those sums — can't average the facilities' own
// percentages, they're relative to each facility's own patient count.
const aggregatePatientMonitoringFrequency = (facilities) => {
  const counts = {};
  RECORDING_BUCKETS.forEach(({ key }) => {
    counts[key] = 0;
  });
  let totalUniquePatients = 0;

  facilities.forEach((f) => {
    const freq = f.patient_monitoring_frequency || {};
    RECORDING_BUCKETS.forEach(({ key }) => {
      counts[key] += freq[key]?.count || 0;
    });
    totalUniquePatients += freq.total_unique_patients || 0;
  });

  const result = {};
  RECORDING_BUCKETS.forEach(({ key }) => {
    result[key] = {
      count: counts[key],
      percentage:
        totalUniquePatients > 0 ? (counts[key] / totalUniquePatients) * 100 : 0,
    };
  });
  return result;
};

const aggregateFacilityStatusCounts = (facilities) => ({
  active: facilities.filter((f) => f.is_active).length,
  inactive: facilities.filter((f) => !f.is_active).length,
});

// A district counts as "active" if any facility within it is active — the
// API has no district_status field of its own.
const aggregateDistrictStatusCounts = (facilities) => {
  const districtActiveMap = {};
  facilities.forEach((f) => {
    const dist = f.district || "Unknown";
    districtActiveMap[dist] = districtActiveMap[dist] || !!f.is_active;
  });
  const values = Object.values(districtActiveMap);
  return {
    active: values.filter(Boolean).length,
    inactive: values.filter((v) => !v).length,
  };
};

function BasicAnalytics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const childPlaces = useMemo(() => {
    try {
      return JSON.parse(Cookie.get("child_places") || "[]");
    } catch (error) {
      return [];
    }
  }, []);
  const [facilitiesFromApi, setFacilitiesFromApi] = useState([]);
  const isSuperAdmin = childPlaces.length > 1 || facilitiesFromApi.length > 1;

  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(true);

  // "all" — no date params sent — is the default until the user actively
  // picks a date filter, rather than implicitly scoping to the current month.
  const [globalDateFilter, setGlobalDateFilter] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");

  const [selectedFacility, setSelectedFacility] = useState("all");

  const isFiltered =
    selectedDistrict !== "all" ||
    selectedFacility !== "all" ||
    globalDateFilter !== "all" ||
    !!startDate ||
    !!endDate;

  const resetAllFilters = () => {
    setSelectedDistrict("all");
    setSelectedFacility("all");
    setGlobalDateFilter("all");
    setStartDate(null);
    setEndDate(null);
    document.activeElement?.blur();
  };

  useResetOnBack(isFiltered, resetAllFilters);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = sessionStorage.getItem("sidebarCollapsed");
    return stored ? JSON.parse(stored) : false;
  });

  const [patientData, setPatientData] = useState([]);
  const [monitoringData, setMonitoringData] = useState([]);
  const [ctgSessionsData, setCtgSessionsData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [availableFacilityIds, setAvailableFacilityIds] = useState([]);
  const [isPatientLoading, setIsPatientLoading] = useState(true);
  const [isMonitoringLoading, setIsMonitoringLoading] = useState(true);

  const [patientError, setPatientError] = useState(false);
  const [monitoringError, setMonitoringError] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  // 🎯 MOCK DATA FOR TESTING TRUNCATION - Set to true to enable
  const ENABLE_MOCK_DATA = false;

  const mockPatientData = [
    {
      id: "Very Long Hospital Name That Should Be Truncated In Display",
      patients: 1234567890123,
    },
    {
      id: "Another Extremely Long Facility Name For Testing Purposes",
      patients: 987654321098,
    },
    {
      id: "Super Long Medical Center Name That Exceeds Normal Length",
      patients: 555666777888,
    },
    { id: "Short Name", patients: 123 },
    { id: "Medium Length Hospital", patients: 456789 },
  ];

  const mockMonitoringData = [
    {
      id: "Very Long Hospital Name That Should Be Truncated In Display",
      hours: 123456789.123,
    },
    {
      id: "Another Extremely Long Facility Name For Testing Purposes",
      hours: 987654321.987,
    },
    {
      id: "Super Long Medical Center Name That Exceeds Normal Length",
      hours: 555666777.888,
    },
    { id: "Short Name", hours: 123.45 },
    { id: "Medium Length Hospital", hours: 4567.89 },
  ];

  const mockSummaryData = {
    total_devices: 1234567890123,
    total_mothers_added: 9876543210987,
    total_alerts_generated: 5556667778889,
    total_hours_monitored: 1111222233334,
  };

  const requestCacheRef = useRef(new Map());
  const facilityOverviewRequestIdRef = useRef(0);
  const availableFacilitiesRequestIdRef = useRef(0);
  const chartFacilitiesRequestIdRef = useRef(0);
  const deviceOverviewRequestIdRef = useRef(0);

  const token = Cookie.get("access_token");

  const hospitalDetails = JSON.parse(Cookie.get("hospital_detail") || "{}");

  const loggedInPlaceId = hospitalDetails?.hospitalID;
  // console.log("PLACE ID:", loggedInPlaceId);

  useEffect(() => {
    const handleSidebarStateChange = (event) => {
      setIsSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener("sidebarStateChange", handleSidebarStateChange);

    return () => {
      window.removeEventListener(
        "sidebarStateChange",
        handleSidebarStateChange,
      );
    };
  }, []);

  const handleCalendarOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
    setIsSelectingStartDate(true);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setGlobalDateFilter("all");
    setIsSelectingStartDate(true);
  };

  const handleDateSelect = (date) => {
    if (isSelectingStartDate) {
      setStartDate(date);
      // Clear previous end date to avoid fetching with partial stale ranges
      setEndDate(null);
      setGlobalDateFilter("custom");
      setIsSelectingStartDate(false);
    } else {
      setEndDate(date);
      setGlobalDateFilter("custom");
      if (
        startDate &&
        date &&
        (date.isAfter(startDate) || date.isSame(startDate))
      ) {
        handleCalendarClose();
      } else {
        // If end date is before start date, swap them
        setEndDate(startDate);
        setStartDate(date);
        handleCalendarClose();
      }
    }
  };

  const getDateRange = (filterType) => {
    const today = dayjs();
    if (filterType === "all") return { start: null, end: null };
    if (filterType === "day") return { start: today, end: today };
    if (filterType === "yesterday") {
      const yesterday = today.subtract(1, "day");
      return { start: yesterday, end: yesterday };
    }
    if (filterType === "week")
      return { start: today.subtract(6, "day"), end: today };
    if (filterType === "last15days")
      return { start: today.subtract(14, "day"), end: today };
    if (filterType === "last30days")
      return { start: today.subtract(29, "day"), end: today };
    if (filterType === "month")
      return { start: today.startOf("month"), end: today };

    if (filterType === "custom") return { start: startDate, end: endDate };
    return { start: today, end: today };
  };

  const getApiParams = (filterType) => {
    const { start, end } = getDateRange(filterType);
    return {
      // No place_ids when "all" — API will return data for all places
      ...(selectedFacility && selectedFacility !== "all"
        ? { place_ids: selectedFacility }
        : {}),
      ...(start &&
        end && {
          from_time: start.format("YYYY-MM-DD"),
          to_time: end.add(1, "day").format("YYYY-MM-DD"),
        }),
    };
  };

  const getHeaderParams = () => {
    const { start, end } = getDateRange(globalDateFilter);
    return {
      ...(selectedFacility && selectedFacility !== "all"
        ? { place_ids: selectedFacility }
        : {}),
      ...(start &&
        end && {
          from_time: start.format("YYYY-MM-DD"),
          to_time: end.add(1, "day").format("YYYY-MM-DD"),
        }),
    };
  };

  const getAvailableFacilitiesParams = () => {
    const { start, end } = getDateRange(globalDateFilter);
    return {
      ...(start &&
        end && {
          from_time: start.format("YYYY-MM-DD"),
          to_time: end.add(1, "day").format("YYYY-MM-DD"),
        }),
    };
  };

  // v1 facility-overview returns a flat `results[]` with no district field.
  // v2 (used by UltraAdmin) nests facilities inside `zones[]`, each carrying
  // district. Handle either shape so this works regardless of what v2 returns
  // for a SuperAdmin (who may not have a zones wrapper at all).
  const extractFacilitiesList = (data) => {
    if (Array.isArray(data?.zones)) {
      return data.zones.flatMap((z) => z.facilities || []);
    }
    return data?.results || [];
  };

  const buildQueryString = (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        query.append(key, String(value));
      }
    });
    return query.toString();
  };

  const fetchAnalytics = async (endpoint, params = {}, version = "v1") => {
    const queryString = buildQueryString(params);
    const basePath = version === "v2" ? "api/analytics/v2/" : "api/analytics/";
    const url = `${urls.DEV_URL}${basePath}${endpoint}/?${
      queryString ? queryString : ""
    }`;
    const cacheKey = `${version}:${endpoint}?${queryString}`;

    if (requestCacheRef.current.has(cacheKey)) {
      return requestCacheRef.current.get(cacheKey);
    }

    const requestPromise = fetch(url, {
      headers: { Authorization: `Bearer ${Cookie.get("access_token")}` },
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Request failed (${res.status}) for ${endpoint}`);
      }
      return res.json();
    });

    requestCacheRef.current.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      requestCacheRef.current.delete(cacheKey);
    }
  };

  // Device-level drill-down (plain Admin, or a SuperAdmin who selected a
  // specific facility): one shared device-overview fetch feeds Patient
  // Stats, Monitoring Stats, and CTG Sessions, instead of three separate
  // calls. avg_monitoring_hours_per_device doesn't exist per-device (only in
  // the response's root `summary`, a facility-wide aggregate) — a single
  // device's own average is just its own hours_monitored (dividing by 1
  // device is mathematically valid).
  const fetchDeviceOverview = async () => {
    // Its own dedicated ref, matching fetchChartFacilities' pattern below —
    // one ref per independent fetch, never shared/synced across two.
    const requestId = ++deviceOverviewRequestIdRef.current;
    setIsPatientLoading(true);
    setIsMonitoringLoading(true);
    setPatientError(false);
    setMonitoringError(false);
    try {
      // 🎯 Use mock data if enabled
      if (ENABLE_MOCK_DATA) {
        setTimeout(() => {
          if (requestId === deviceOverviewRequestIdRef.current) {
            setPatientData(mockPatientData);
            setMonitoringData(mockMonitoringData);
            setIsPatientLoading(false);
            setIsMonitoringLoading(false);
          }
        }, 500);
        return;
      }

      const params = getApiParams(globalDateFilter);
      const data = await fetchAnalytics("device-overview", params);
      if (requestId !== deviceOverviewRequestIdRef.current) return;

      // device-overview returns first_name (worker name), NOT place_name
      // Backend sometimes returns a worker_id: null "Unassigned / Other"
      // row (unmapped device) — drop it until that's fixed server-side.
      const results = (data.results || []).filter(
        (item) => item.worker_id != null,
      );

      setPatientData(
        results.map((item) => ({
          id: item.first_name,
          patients: item.mothers_added,
        })),
      );

      setMonitoringData(
        results.map((item) => ({
          id: item.first_name,
          hours: item.hours_monitored,
          avgPerDevice: item.hours_monitored ?? 0,
          avgPerPregnantWoman: item.avg_monitoring_hours_per_pregnant_woman ?? 0,
        })),
      );

      setCtgSessionsData(
        results.map((item) => ({
          id: item.first_name,
          sessions: item.ctg_sessions || 0,
        })),
      );
    } catch (err) {
      console.error(err);
      if (requestId === deviceOverviewRequestIdRef.current) {
        setPatientData([]);
        setMonitoringData([]);
        setCtgSessionsData([]);
        setPatientError(true);
        setMonitoringError(true);
      }
    } finally {
      if (requestId === deviceOverviewRequestIdRef.current) {
        setIsPatientLoading(false);
        setIsMonitoringLoading(false);
      }
    }
  };

  const fetchFacilityOverview = async () => {
    const requestId = ++facilityOverviewRequestIdRef.current;
    setIsSummaryLoading(true);
    try {
      // 🎯 Use mock data if enabled
      if (ENABLE_MOCK_DATA) {
        setTimeout(() => {
          if (requestId === facilityOverviewRequestIdRef.current) {
            setSummaryData(mockSummaryData);
            setIsSummaryLoading(false);
          }
        }, 300);
        return;
      }

      const data = await fetchAnalytics(
        "facility-overview",
        getHeaderParams(),
        "v2",
      );
      // Ignore stale responses from older requests
      if (requestId !== facilityOverviewRequestIdRef.current) return;
      setSummaryData(data.summary);
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === facilityOverviewRequestIdRef.current) {
        setIsSummaryLoading(false);
      }
    }
  };

  const fetchAvailableFacilities = async () => {
    const requestId = ++availableFacilitiesRequestIdRef.current;
    try {
      const data = await fetchAnalytics(
        "facility-overview",
        getAvailableFacilitiesParams(),
        "v2",
      );
      if (requestId !== availableFacilitiesRequestIdRef.current) return;
      const facilitiesList = extractFacilitiesList(data);
      const allIds = facilitiesList.map((item) => String(item.place_id));
      setAvailableFacilityIds(allIds);
      setFacilitiesFromApi(
        facilitiesList.map((item) => ({
          id: item.place_id,
          name: item.place_name || "",
          district: item.district || "",
        })),
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Raw per-facility rows (full objects, all v2 fields) from the last fetch —
  // the derived effects below group these by district or filter them,
  // depending on selectedDistrict, without needing a re-fetch. Replaces two
  // near-identical parallel fetches (one for patients, one for monitoring)
  // with a single shared one, per the file's own long-standing TODO above.
  const [chartFacilities, setChartFacilities] = useState([]);

  const fetchChartFacilities = async (filterType) => {
    const requestId = ++chartFacilitiesRequestIdRef.current;
    setIsPatientLoading(true);
    setIsMonitoringLoading(true);
    setPatientError(false);
    setMonitoringError(false);
    try {
      // 🎯 Use mock data if enabled
      if (ENABLE_MOCK_DATA) {
        setTimeout(() => {
          if (requestId === chartFacilitiesRequestIdRef.current) {
            setPatientData(mockPatientData);
            setMonitoringData(mockMonitoringData);
            setIsPatientLoading(false);
            setIsMonitoringLoading(false);
          }
        }, 500);
        return;
      }

      const params = getApiParams(filterType);
      const data = await fetchAnalytics("facility-overview", params, "v2");
      if (requestId !== chartFacilitiesRequestIdRef.current) return;
      setChartFacilities(extractFacilitiesList(data));
    } catch (err) {
      console.error(err);
      if (requestId === chartFacilitiesRequestIdRef.current) {
        setChartFacilities([]);
        setPatientError(true);
        setMonitoringError(true);
      }
    } finally {
      if (requestId === chartFacilitiesRequestIdRef.current) {
        setIsPatientLoading(false);
        setIsMonitoringLoading(false);
      }
    }
  };

  useEffect(() => {
    if (globalDateFilter === "custom" && (!startDate || !endDate)) return;
    if (isSuperAdmin && (!selectedFacility || selectedFacility === "all")) {
      fetchChartFacilities(globalDateFilter);
    } else {
      fetchDeviceOverview();
    }
  }, [globalDateFilter, startDate, endDate, selectedFacility, isSuperAdmin]);

  // District-level aggregation for the top-of-hierarchy view — sums each
  // district's facilities when none is selected, or lists that district's
  // facilities individually once one is picked.
  useEffect(() => {
    if (!isSuperAdmin || selectedFacility !== "all") return;
    if (selectedDistrict === "all") {
      const districtMap = {};
      chartFacilities.forEach((f) => {
        const dist = f.district || "Unknown";
        districtMap[dist] = (districtMap[dist] || 0) + (f.mothers_added || 0);
      });
      setPatientData(
        Object.entries(districtMap).map(([district, patients]) => ({
          id: district,
          patients,
        })),
      );
    } else {
      setPatientData(
        chartFacilities
          .filter((f) => f.district === selectedDistrict)
          .map((f) => ({
            id: f.place_name,
            placeId: f.place_id,
            patients: f.mothers_added || 0,
          })),
      );
    }
  }, [chartFacilities, selectedDistrict, selectedFacility, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || selectedFacility !== "all") return;
    if (selectedDistrict === "all") {
      const districtMap = {};
      chartFacilities.forEach((f) => {
        const dist = f.district || "Unknown";
        if (!districtMap[dist]) {
          districtMap[dist] = { hours: 0, deviceUnits: 0, mothers: 0 };
        }
        districtMap[dist].hours += f.hours_monitored || 0;
        // Derive the device count implied by the facility's own avg field
        // instead of reading a devices/total_devices field directly — stays
        // correct regardless of how the backend defines "per device".
        if (f.avg_monitoring_hours_per_device > 0) {
          districtMap[dist].deviceUnits +=
            (f.hours_monitored || 0) / f.avg_monitoring_hours_per_device;
        }
        districtMap[dist].mothers += f.mothers_added || 0;
      });
      setMonitoringData(
        Object.entries(districtMap).map(([district, totals]) => ({
          id: district,
          hours: parseFloat(totals.hours.toFixed(2)),
          avgPerDevice:
            totals.deviceUnits > 0
              ? parseFloat((totals.hours / totals.deviceUnits).toFixed(2))
              : 0,
          avgPerPregnantWoman:
            totals.mothers > 0
              ? parseFloat((totals.hours / totals.mothers).toFixed(2))
              : 0,
        })),
      );
    } else {
      setMonitoringData(
        chartFacilities
          .filter((f) => f.district === selectedDistrict)
          .map((f) => ({
            id: f.place_name,
            placeId: f.place_id,
            hours: f.hours_monitored || 0,
            avgPerDevice: f.avg_monitoring_hours_per_device || 0,
            avgPerPregnantWoman: f.avg_monitoring_hours_per_pregnant_woman || 0,
          })),
      );
    }
  }, [chartFacilities, selectedDistrict, selectedFacility, isSuperAdmin]);

  // CTG sessions chart — same categories/hierarchy as Patient Stats.
  useEffect(() => {
    // Device-level drill-down (plain Admin, or a SuperAdmin who selected a
    // specific facility): fetchDeviceOverview already set real
    // ctgSessionsData from device-overview's own ctg_sessions field.
    if (!isSuperAdmin || selectedFacility !== "all") return;
    if (selectedDistrict === "all") {
      const districtMap = {};
      chartFacilities.forEach((f) => {
        const dist = f.district || "Unknown";
        districtMap[dist] = (districtMap[dist] || 0) + (f.ctg_sessions || 0);
      });
      setCtgSessionsData(
        Object.entries(districtMap).map(([district, sessions]) => ({
          id: district,
          sessions,
        })),
      );
    } else {
      setCtgSessionsData(
        chartFacilities
          .filter((f) => f.district === selectedDistrict)
          .map((f) => ({
            id: f.place_name,
            placeId: f.place_id,
            sessions: f.ctg_sessions || 0,
          })),
      );
    }
  }, [chartFacilities, selectedDistrict, selectedFacility, isSuperAdmin]);

  useEffect(() => {
    // Skip header fetch while only one date is selected
    if (globalDateFilter === "custom" && (!startDate || !endDate)) return;
    fetchFacilityOverview();
  }, [globalDateFilter, selectedFacility, startDate, endDate]);

  useEffect(() => {
    // Keep dropdown source independent from selected facility
    if (globalDateFilter === "custom" && (!startDate || !endDate)) return;
    fetchAvailableFacilities();
  }, [globalDateFilter, startDate, endDate]);

  useEffect(() => {
    if (
      selectedFacility !== "all" &&
      availableFacilityIds.length > 0 &&
      !availableFacilityIds.includes(String(selectedFacility))
    ) {
      setSelectedFacility("all");
    }
  }, [availableFacilityIds, selectedFacility]);

  const facilityOptions =
    childPlaces && childPlaces.length > 0 ? childPlaces : facilitiesFromApi;

  const filteredChildPlaces =
    availableFacilityIds.length > 0
      ? facilityOptions.filter((place) =>
          availableFacilityIds.includes(String(place.id)),
        )
      : facilityOptions;

  // District membership only comes from facilitiesFromApi (childPlaces cookie
  // doesn't carry it), so once a district is picked we source the facility
  // list from there instead of the usual childPlaces-first fallback.
  const districtOptions = useMemo(
    () => [...new Set(facilitiesFromApi.map((f) => f.district).filter(Boolean))],
    [facilitiesFromApi],
  );

  const facilityOptionsForDropdown =
    selectedDistrict === "all"
      ? filteredChildPlaces
      : facilitiesFromApi.filter((f) => f.district === selectedDistrict);

  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedFacility("all");
  };

  // Picking a facility directly (while District is still "all") should
  // back-fill District to the facility's actual district, matching
  // UltraAdminAnalytics.jsx's equivalent zone+district back-fill.
  const handleFacilitySelect = (facilityId) => {
    if (facilityId === "all") {
      setSelectedFacility("all");
      return;
    }
    const facility = facilitiesFromApi.find((f) => f.id === facilityId);
    if (facility?.district) setSelectedDistrict(facility.district);
    setSelectedFacility(facilityId);
  };

  const getCurrentHierarchyLevel = () => {
    if (selectedFacility !== "all") return "device";
    if (isSuperAdmin && selectedDistrict !== "all") return "facility";
    if (isSuperAdmin) return "district";
    return "device";
  };

  const handleBarClick = (barData) => {
    try {
      if (
        !barData ||
        barData.id === null ||
        barData.id === undefined ||
        barData.id === ""
      ) {
        console.error("Bar click: invalid bar data structure", barData);
        return;
      }

      const level = getCurrentHierarchyLevel();
      if (level === "device") return;

      if (level === "district") {
        if (districtOptions.includes(barData.id)) {
          handleDistrictChange(barData.id);
        }
        return;
      }

      setSelectedFacility(barData.placeId);
    } catch (err) {
      console.error(
        "Bar click: unexpected error while processing drill-down",
        err,
      );
    }
  };

  const formatDateDisplay = () => {
    if (!startDate || !endDate) return "Select Date";
    if (startDate.isSame(endDate)) {
      return startDate.format("D MMM YYYY");
    }
    const startFormat =
      startDate.year() === endDate.year() ? "D MMM" : "D MMM YYYY";
    return `${startDate.format(startFormat)} - ${endDate.format("D MMM YYYY")}`;
  };

  const formatRange = (start, end) => {
    if (!start || !end) return null;
    if (start.isSame(end, "day")) return start.format("D MMM YYYY");
    const startFormat = start.year() === end.year() ? "D MMM" : "D MMM YYYY";
    return `${start.format(startFormat)} - ${end.format("D MMM YYYY")}`;
  };
  const { start: activeRangeStart, end: activeRangeEnd } =
    getDateRange(globalDateFilter);
  const activeDateRangeDisplay =
    formatRange(activeRangeStart, activeRangeEnd) || "Till Date";

  const hierarchyLevel = getCurrentHierarchyLevel();

  // Facilities in the currently selected district — only meaningful at the
  // "facility" hierarchy level (SuperAdmin, a district chosen but no
  // specific facility yet).
  const districtFacilities = chartFacilities.filter(
    (f) => f.district === selectedDistrict,
  );

  // Header cards — summaryData is already correctly scoped by the backend
  // for the unfiltered (district="all") and device (a facility selected via
  // place_ids) cases; only the "a district is selected, no facility yet"
  // case needs client-side self-aggregation, since the header fetch doesn't
  // rescope by district.
  const filteredSummary = useMemo(() => {
    if (hierarchyLevel !== "facility") {
      return summaryData;
    }
    return {
      total_devices: districtFacilities.reduce(
        (sum, f) => sum + (f.total_devices || 0),
        0,
      ),
      total_hospitals: districtFacilities.length,
      total_alerts_generated: districtFacilities.reduce(
        (sum, f) => sum + (f.alerts_generated || 0),
        0,
      ),
      total_hours_monitored: parseFloat(
        districtFacilities
          .reduce((sum, f) => sum + (f.hours_monitored || 0), 0)
          .toFixed(2),
      ),
      total_ctg_sessions: districtFacilities.reduce(
        (sum, f) => sum + (f.ctg_sessions || 0),
        0,
      ),
      total_unique_patients_monitored: districtFacilities.reduce(
        (sum, f) => sum + (f.unique_patients_monitored || 0),
        0,
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyLevel, selectedDistrict, chartFacilities, summaryData]);

  let leftDonut = null; // null hides the left donut (device level, or plain Admin)
  let deviceStatusSegments = { active: 0, disconnected: 0 };
  let traceCategoryCounts = {};
  let patientMonitoringFrequency = {};

  if (hierarchyLevel === "district") {
    leftDonut = {
      title: "District status",
      label: "District",
      ...aggregateDistrictStatusCounts(chartFacilities),
    };
    deviceStatusSegments = aggregateDeviceStatus(chartFacilities);
    traceCategoryCounts = aggregateTraceCategory(chartFacilities);
    patientMonitoringFrequency = aggregatePatientMonitoringFrequency(chartFacilities);
  } else if (hierarchyLevel === "facility") {
    leftDonut = {
      title: "Facility status",
      label: "Facility",
      ...aggregateFacilityStatusCounts(districtFacilities),
    };
    deviceStatusSegments = aggregateDeviceStatus(districtFacilities);
    traceCategoryCounts = aggregateTraceCategory(districtFacilities);
    patientMonitoringFrequency = aggregatePatientMonitoringFrequency(districtFacilities);
  } else {
    // "device" level — a specific facility selected (SuperAdmin) or a plain
    // Admin's own single facility. summaryData is real here either way: the
    // header fetch always uses facility-overview (never device-overview),
    // and already scopes to place_ids when a facility is selected.
    deviceStatusSegments = {
      active: summaryData.device_status?.active || 0,
      disconnected: summaryData.device_status?.disconnected || 0,
    };
    traceCategoryCounts = summaryData.trace_category || {};
    patientMonitoringFrequency = summaryData.patient_monitoring_frequency || {};
  }

  const alertOverviewCategories = [
    { label: "Normal", value: traceCategoryCounts.normal || 0, color: "#6FCF97" },
    {
      label: "Suspicious",
      value: traceCategoryCounts.suspicious || 0,
      color: "#F2C265",
    },
    {
      label: "Pathological",
      value: traceCategoryCounts.pathological || 0,
      color: "#F2765A",
    },
  ];

  const recordingsTiles = RECORDING_BUCKETS.map(({ key, label }) => {
    const bucket = patientMonitoringFrequency[key] || {};
    return {
      label,
      percentage: `${(bucket.percentage || 0).toFixed(2)}%`,
      count: (bucket.count || 0).toLocaleString(),
    };
  });

  const open = Boolean(anchorEl);
  const id = open ? "custom-date-popover" : undefined;

  return (
    <div
      className={`analytics ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <div className="analytics">
        <div className="analytics-section">
          <div className="analytics-page basic-analytics-wrapper">
            {/* Top Heading & Tools */}
            <div className="basic-dashboard-header">
              <div className="basic-dashboard-title">Dashboard</div>

              <div className="header-controls">
                {/* Date Display and Clear Button - Only show when dates are selected */}
                {startDate && endDate && (
                  <button className="clear-date-btn" onClick={handleClearDates}>
                    ✕ Clear Custom Date
                  </button>
                )}

                <button
                  className={`custom-date-selector-btn ${open ? "active" : ""} ${
                    startDate && endDate ? "date-chosen" : ""
                  }`}
                  onClick={handleCalendarOpen}
                  aria-describedby={id}
                >
                  <CalendarMonthIcon sx={{ color: "#6e6e6e", fontSize: 22 }} />
                </button>

                {/* Global Date Filter — present for Admin and SuperAdmin alike */}
                <FormControl className="facility-dropdown">
                  <Select
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      transformOrigin: { vertical: "top", horizontal: "right" },
                      PaperProps: { style: { marginTop: 4, maxWidth: 280 } },
                    }}
                    value={globalDateFilter}
                    onChange={(e) => {
                      setGlobalDateFilter(e.target.value);
                      setStartDate(null);
                      setEndDate(null);
                      setIsSelectingStartDate(true);
                    }}
                    className="facility-select"
                    renderValue={(selected) =>
                      selected === "custom"
                        ? formatDateDisplay()
                        : DATE_FILTER_LABELS[selected] || "Till Date"
                    }
                    sx={{
                      minWidth: globalDateFilter === "custom" ? 240 : 150,
                      height: 40,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 32px 8px 12px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="all">Till Date</MenuItem>
                    <MenuItem value="month">Current Month</MenuItem>
                    <MenuItem value="day">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="week">Last 7 days</MenuItem>
                    <MenuItem value="last15days">Last 15 Days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                    <MenuItem value="custom" disabled>
                      {formatDateDisplay()}
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* District Dropdown for SuperAdmin */}
                {isSuperAdmin && (
                  <FormControl className="facility-dropdown">
                    <Select
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "right",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "right",
                        },
                        PaperProps: {
                          style: {
                            marginTop: 4,
                            maxWidth: 280,
                          },
                        },
                      }}
                      value={selectedDistrict}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected || selected === "all")
                          return "All Districts";
                        return (
                          <span
                            title={selected}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                              maxWidth: "140px",
                            }}
                          >
                            {selected}
                          </span>
                        );
                      }}
                      className="facility-select"
                      sx={{
                        minWidth: 150,
                        height: 40,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          borderWidth: "1px",
                        },
                        "& .MuiSelect-select": {
                          padding: "8px 32px 8px 12px",
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <MenuItem value="all">All Districts</MenuItem>
                      {districtOptions.map((d) => (
                        <MenuItem
                          key={d}
                          value={d}
                          title={d}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#F6F2FF",
                              color: "rgba(108, 23, 217, 1)",
                              fontWeight: 500,
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#F6F2FF",
                              color: "rgba(108, 23, 217, 1)",
                            },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                            maxWidth: "100%",
                          }}
                        >
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Facility Dropdown for SuperAdmin */}
                {isSuperAdmin && (
                  <FormControl className="facility-dropdown">
                    <Select
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "right",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "right",
                        },
                        PaperProps: {
                          style: {
                            marginTop: 4,
                            maxWidth: 280,
                          },
                        },
                      }}
                      value={selectedFacility}
                      onChange={(e) => handleFacilitySelect(e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected || selected === "all") return "All";

                        const place = facilityOptionsForDropdown.find(
                          (p) => p.id === selected,
                        );
                        const name = place?.name || "";

                        return (
                          <span
                            title={name}
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                              maxWidth: "140px",
                            }}
                          >
                            {name}
                          </span>
                        );
                      }}
                      className="facility-select"
                      sx={{
                        minWidth: 190,
                        height: 40,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          borderWidth: "1px",
                        },
                        "& .MuiSelect-select": {
                          padding: "8px 32px 8px 12px",
                          display: "flex",
                          alignItems: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {facilityOptionsForDropdown.map((place) => (
                        <MenuItem
                          key={place.id}
                          value={place.id}
                          title={place.name}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "#F6F2FF",
                              color: "rgba(108, 23, 217, 1)",
                              fontWeight: 500,
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#F6F2FF",
                              color: "rgba(108, 23, 217, 1)",
                            },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                            maxWidth: "100%",
                          }}
                        >
                          {place.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>

              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleCalendarClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  className: "custom-calendar-popover",
                }}
              >
                <div className="date-range-picker">
                  {startDate && endDate && (
                    <div className="selected-range-display">
                      Selected : {formatDateDisplay()}
                    </div>
                  )}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      className="custom-mui-calendar"
                      value={isSelectingStartDate ? startDate : endDate}
                      onChange={handleDateSelect}
                      disableFuture
                      slots={{ day: CustomDay }}
                      slotProps={{
                        day: (dayProps) => ({
                          ...dayProps,
                          startDate,
                          endDate,
                        }),
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </Popover>
            </div>

            {/* Top Stats Cards Container */}
            <AnalyticsStatCards
              values={{
                total_hospitals: filteredSummary.total_hospitals,
                total_devices: filteredSummary.total_devices,
                total_alerts_generated: filteredSummary.total_alerts_generated,
                total_hours_monitored: filteredSummary.total_hours_monitored,
                total_ctg_sessions: filteredSummary.total_ctg_sessions,
                unique_patients_monitored:
                  filteredSummary.total_unique_patients_monitored,
              }}
              isLoading={isSummaryLoading}
            />

            <PatientStats
              data={patientData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isPatientLoading}
              hasError={patientError}
              onBarClick={handleBarClick}
            />

            <MonitoringStats
              data={monitoringData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isMonitoringLoading}
              hasError={monitoringError}
              onBarClick={handleBarClick}
            />

            <AlertOverview
              categories={alertOverviewCategories}
              dateRangeDisplay={activeDateRangeDisplay}
            />

            <UniquePatientMonitored
              tiles={recordingsTiles}
              dateRangeDisplay={activeDateRangeDisplay}
            />

            <CtgSessions
              data={ctgSessionsData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isPatientLoading}
              hasError={patientError}
              onBarClick={handleBarClick}
            />

            <div className="status-donut-row">
              {/* District/Facility status has no meaning for a single-facility
                  Admin, and is hidden once a SuperAdmin drills into one
                  specific facility (leftDonut is null in both cases). */}
              {leftDonut && (
                <StatusDonutCard
                  title={leftDonut.title}
                  dateRangeDisplay={activeDateRangeDisplay}
                  segments={[
                    {
                      name: `Active ${leftDonut.label}`,
                      legendLabel: "Active",
                      value: leftDonut.active,
                      color: "#9bfdf5",
                    },
                    {
                      name: `Inactive ${leftDonut.label}`,
                      legendLabel: "Inactive",
                      value: leftDonut.inactive,
                      color: "#F792B0",
                    },
                  ]}
                />
              )}

              <StatusDonutCard
                title="Device status"
                dateRangeDisplay={activeDateRangeDisplay}
                segments={[
                  {
                    name: "Active Devices",
                    legendLabel: "Active Devices",
                    value: deviceStatusSegments.active,
                    color: "#C9E7C1",
                  },
                  {
                    name: "Inactive Devices",
                    legendLabel: "Inactive Devices",
                    value: deviceStatusSegments.disconnected,
                    color: "#EA8194",
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BasicAnalytics;
